import { createSelector } from 'reselect';
import { getCallState } from './call-state';
import { SELF_PARTICIPANT_ID } from '../../call-mobile/constants';
import { orderBy } from 'lodash';
import { getSecondParam, localeService, UserRole } from '../../core';
import { CONNECTION_LEVELS_TO_SHOW_PARTICIPANT_NPI, StreamType, } from '../constants';
import { hasCameraAccess, hasMicrophoneAccess } from '../../devices/selectors';
export const getActiveStreamId = (state) => getCallState(state).activeStreamId;
export const getCurrentStreamId = (state) => getCallState(state).currentStreamId;
export const getPinnedStreamId = (state) => getCallState(state).pinnedStreamId;
export const getSelfParticipant = createSelector([getCallState, hasCameraAccess, hasMicrophoneAccess], (callState, cameraAccess, microphoneAccess) => ({
    ...callState.selfParticipant,
    hasCameraAccess: cameraAccess,
    hasMicrophoneAccess: microphoneAccess,
}));
export const getParticipants = (state) => getCallState(state).participants;
export const getConnectionStatus = (state) => getCallState(state).connectionStatus;
export const getParticipantById = createSelector(getParticipants, getSecondParam, (participants, participantId) => participants.find((p) => p.id === participantId));
export const getNoVideoMessage = createSelector(getSelfParticipant, getSecondParam, (self, participant) => {
    if (!participant.hasVideo && self.id !== participant.id) {
        return localeService.formatMessageById('video-chat.camera.mobile-message', {
            name: `<strong>${participant.name}</strong>`,
        });
    }
    else {
        return null;
    }
});
export const hasParticipantNpiIcon = createSelector(getConnectionStatus, getSecondParam, (obj, participantUuid) => {
    return CONNECTION_LEVELS_TO_SHOW_PARTICIPANT_NPI.includes(obj[participantUuid]);
});
export const isParticipantStreamTypeACamera = (participant) => participant.streamType === StreamType.Camera;
export const isParticipantADoctor = (participant) => participant.role === UserRole.Doctor;
export const isParticipantCurrent = (p, activeStreamId, pinnedStreamId) => {
    const currentStreamId = pinnedStreamId || activeStreamId;
    return p.id === currentStreamId;
};
export const getSortedParticipants = createSelector(getParticipants, getActiveStreamId, getPinnedStreamId, (participants, activeStreamId, pinnedStreamId) => orderBy(participants, [
    isParticipantStreamTypeACamera,
    isParticipantADoctor,
    (p) => !isParticipantCurrent(p, activeStreamId, pinnedStreamId),
    'lastActivity',
], ['desc', 'desc', 'desc', 'desc']));
/**
 * @Description Used in the new mobile UI when Whiteboard covers the main view.
 *              In this case as pinned / active person is not visible it must have the bigger priority
 *              to appear at the bottom list.
 *
 */
export const getSortedParticipantsWhenCallFrameIsActive = createSelector(getParticipants, getActiveStreamId, getPinnedStreamId, (participants, activeStreamId, pinnedStreamId) => orderBy(participants, [
    isParticipantStreamTypeACamera,
    isParticipantADoctor,
    (p) => isParticipantCurrent(p, activeStreamId, pinnedStreamId),
    'lastActivity',
], ['desc', 'desc', 'desc', 'desc']));
export const getAlphabeticallySortedParticipants = createSelector(getParticipants, (participants) => [...participants].sort((p1, p2) => p1.name.localeCompare(p2.name)));
export const getCurrentParticipant = createSelector(getCurrentStreamId, getParticipants, getSelfParticipant, (currentId, participants, selfParticipant) => {
    if (currentId === SELF_PARTICIPANT_ID) {
        return selfParticipant;
    }
    return participants.find((p) => p.id === currentId);
});
export const getActiveParticipant = createSelector(getActiveStreamId, getParticipants, getSelfParticipant, (activeId, participants, selfParticipant) => {
    if (activeId === SELF_PARTICIPANT_ID) {
        return selfParticipant;
    }
    return participants.find((p) => p.id === activeId);
});
export const isParticipantPinned = createSelector(getPinnedStreamId, getSecondParam, (pinnedId, participantId) => pinnedId === participantId);
export const getParticipantsPublisherErrors = (state) => getCallState(state).participantsPublisherErrors;
export const hasSelfPublisherError = (state) => getCallState(state).isSelfPublisherError;
export const hasPublisherError = createSelector(getParticipantsPublisherErrors, getSecondParam, (publisherErrors, personId) => publisherErrors.has(personId));
export const getSelfPublisherErrors = (state) => getCallState(state).selfPublisherErrors;
export const isSelfPreviewVisible = (state) => getCallState(state).isSelfPreviewVisible;
export const getBackgroundEffect = (state) => getCallState(state).backgroundEffect;
export const isPiPTurnedOn = (state) => getCallState(state).isPiPTurnedOn;
export const isEndCallAttempt = (state) => getCallState(state).isEndCallAttempt;
export const isSelfPreviewLoading = (state) => getCallState(state).isSelfPreviewLoading;
export const selectPreviewsListVisibility = (state) => getCallState(state).isPreviewsListVisible;
